import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api, LOGO_URL, BRAND } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import Reveal from "@/website/Reveal";
import {
  Wrench, PaintRoller, Lightning, Plant, Sparkle, Television, Drop, Broom,
  CheckCircle, Star, ShieldCheck, Clock, Phone, ArrowUpRight, ArrowRight
} from "@phosphor-icons/react";

const HERO_BG = "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=2400&q=85&auto=format&fit=crop";

const services = [
  { icon: Wrench, name: "General Handyman", desc: "Repairs, mounting, assembly, doors & locks." },
  { icon: Drop, name: "Plumbing Repairs", desc: "Taps, leaks, toilets, blocked drains." },
  { icon: Lightning, name: "Electrical", desc: "Power points, fans, light fittings, smoke alarms." },
  { icon: PaintRoller, name: "Painting", desc: "Interior & exterior, drywall & plastering." },
  { icon: Broom, name: "Pressure Washing", desc: "Driveways, paths, fences, decks." },
  { icon: Plant, name: "Lawn & Garden", desc: "Mowing, hedging, mulching, tidy-ups." },
  { icon: Sparkle, name: "End-of-Lease Clean", desc: "Bond-back cleans, full property reset." },
  { icon: Television, name: "TV / AV & Whitegoods", desc: "Wall-mount, wiring, appliance install." },
];

export default function Home() {
  usePageMeta({ path: "/" });
  const [reviews, setReviews] = useState([]);
  const [googleMeta, setGoogleMeta] = useState({ write_review_url: "", google_url: "", source: "" });
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.15]);

  useEffect(() => {
    api.get("/public/google-reviews").then((r) => {
      setReviews(r.data.reviews || []);
      setGoogleMeta({ write_review_url: r.data.write_review_url, google_url: r.data.google_url, source: r.data.source });
    }).catch((err) => console.warn("[home] reviews fetch failed:", err?.message || err));
  }, []);
  const featured = reviews.filter(r => r.rating >= 4).slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO — full-bleed cinematic, like Kimi */}
      <section data-nav-theme="dark" className="relative h-[100vh] min-h-[680px] overflow-hidden bg-navy-deep text-white">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{background: "linear-gradient(180deg, rgba(5,15,31,0.55) 0%, rgba(11,31,58,0.45) 40%, rgba(5,15,31,0.92) 100%)"}}/>
          <div className="absolute inset-0 mix-blend-multiply bg-navy-deep/30"/>
          <div className="absolute inset-0 grain opacity-70"/>
        </motion.div>

        {/* Centered massive type */}
        <div className="relative h-full flex flex-col items-center justify-center text-center px-5 pt-24 pb-28 md:pt-28 md:pb-32">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 1.2, ease: [0.22,1,0.36,1] }} className="flex flex-col items-center">
            <div className="eyebrow mb-6">{BRAND.area}</div>
            <h1 className="font-display display-hero text-white leading-[0.85] tracking-wide break-words px-4" style={{ letterSpacing: '0.04em' }}>
              TRUE<br/>NORTH
            </h1>
            <div className="font-display tracking-[0.28em] text-white/80 mt-5 md:mt-7 text-base sm:text-xl md:text-3xl break-words px-2">MAINTENANCE</div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="absolute bottom-10 left-0 right-0 flex flex-col items-center gap-3 px-5">
            <Link to="/quote" className="pill-light" data-testid="hero-cta-quote">Get a free quote <ArrowUpRight size={14} weight="bold"/></Link>
            <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="text-xs uppercase tracking-[0.32em] text-white/70 hover:text-white inline-flex items-center gap-2"><Phone size={12} weight="regular"/>{BRAND.phone}</a>
          </motion.div>

          {/* Scroll hint */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.6, y: [0, 8, 0] }} transition={{ delay: 1.5, y: { repeat: Infinity, duration: 2 } }}
            className="absolute bottom-3 right-6 text-[10px] tracking-[0.32em] text-white/50 uppercase rotate-90 origin-right whitespace-nowrap">
            scroll
          </motion.div>
        </div>
      </section>

      {/* Tagline + intro */}
      <section data-nav-theme="light" className="bg-paper min-h-screen flex items-center py-24 md:py-32 px-5 md:px-10">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 items-center w-full">
          <Reveal className="lg:col-span-5">
            <div className="eyebrow eyebrow-dark">Intro · 01</div>
            <h2 className="font-display display-lg text-navy mt-5 leading-[0.95] tracking-wide">{BRAND.tagline.split('—')[0].trim()}.</h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7 lg:pt-3">
            <p className="text-lg md:text-xl text-slate-700 font-light leading-relaxed max-w-2xl">
              True North Maintenance was built on a simple idea: the kind of tradesperson you can actually count on.
              From a leaking tap to a full property tidy-up, we treat every job like it's our own home —
              tidy, professional, properly finished.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <Link to="/about" className="pill-navy">About us <ArrowUpRight size={14} weight="bold"/></Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services list — dark section (alternating) */}
      <section data-nav-theme="dark" className="bg-navy-deep text-white min-h-screen flex items-center py-24 md:py-32 px-5 md:px-10 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none"/>
        <div className="relative max-w-[1400px] mx-auto w-full">
          <Reveal className="flex items-end justify-between flex-wrap gap-4 mb-14">
            <div>
              <div className="eyebrow !text-white/50">Services · 02</div>
              <h2 className="font-display display-xl text-white mt-4 leading-[0.92] tracking-wide">Every job<br/>around the house.</h2>
            </div>
            <Link to="/services" className="text-sm font-medium text-white/80 hover:text-white inline-flex items-center gap-1">All 13 services <ArrowUpRight size={14}/></Link>
          </Reveal>
          <div className="border-t border-white/15">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.name} delay={i * 60}>
                  <Link to="/services" className="group flex items-center justify-between gap-6 py-7 border-b border-white/15 hover:bg-white/[0.04] transition-colors -mx-3 px-3">
                    <div className="flex items-baseline gap-6 md:gap-10 min-w-0">
                      <span className="text-[10px] tracking-[0.24em] font-medium text-white/40">0{i+1}</span>
                      <h3 className="font-display tracking-wide text-white text-3xl md:text-5xl truncate transition-all group-hover:tracking-[0.04em]">{s.name}</h3>
                    </div>
                    <div className="flex items-center gap-5 shrink-0">
                      <span className="hidden md:block text-sm text-white/55 max-w-xs text-right">{s.desc}</span>
                      <Icon size={22} weight="duotone" className="text-white/40 group-hover:text-white transition-colors"/>
                      <ArrowUpRight size={20} weight="thin" className="text-white/30 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"/>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* About promise — light section (alternating) */}
      <section data-nav-theme="light" className="bg-paper text-navy min-h-screen flex items-center py-28 md:py-36 px-5 md:px-10 relative overflow-hidden">
        <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-[0.04]"><img src={LOGO_URL} alt="" className="w-full h-full object-contain compass-rotate"/></div>
        <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-center w-full">
          <Reveal className="lg:col-span-7">
            <div className="eyebrow eyebrow-dark">Promise · 03</div>
            <h2 className="font-display display-xl text-navy mt-5 leading-[0.92] tracking-wide">Tidy.<br/>On time.<br/><span className="text-navy/45">Properly finished.</span></h2>
            <p className="mt-8 text-slate-700 max-w-xl text-lg font-light leading-relaxed">
              If you're not happy with the work, we come back and make it right. No deposits. No surprises on the invoice.
              Just a workmanship guarantee in writing, every job.
            </p>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-5 grid grid-cols-2 gap-6">
            {[{n:"200+", l:"Jobs completed"},{n:"4.9", l:"Customer rating"},{n:"60km", l:"Service radius"},{n:"100%", l:"Workmanship guarantee"}].map((s)=>(
              <div key={s.l} className="border-l border-navy/20 pl-4 py-2">
                <div className="font-display display-md text-navy tracking-wide">{s.n}</div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-medium mt-1">{s.l}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Reviews — dark section (alternating) — always renders to keep rhythm */}
      <section data-nav-theme="dark" className="bg-navy-deep text-white min-h-screen flex items-center py-24 md:py-32 px-5 md:px-10 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none"/>
        <div className="relative max-w-[1400px] mx-auto w-full">
          <Reveal className="text-center mb-14">
            <div className="eyebrow !text-white/50">Reviews · 04</div>
            <h2 className="font-display display-xl text-white mt-4 leading-[0.92] tracking-wide">Trusted across<br/>the region.</h2>
          </Reveal>
          {featured.length > 0 ? (
            <>
              <div className="grid md:grid-cols-3 gap-6">
                {featured.map((r, i) => (
                  <Reveal key={`${r.time || ""}-${r.author_name || i}`} delay={i * 100}>
                    <div className="border-t border-white pt-6 h-full">
                      <div className="flex gap-0.5 mb-4">{Array.from({length:5}).map((_,j)=><Star key={`s-${j}`} size={12} weight={j<r.rating?"fill":"regular"} className={j<r.rating?"text-white":"text-white/25"}/>)}</div>
                      <p className="text-white/85 font-light leading-relaxed text-lg">"{r.text}"</p>
                      <div className="mt-6 text-xs uppercase tracking-[0.22em] text-white/55 font-medium">— {r.author_name}</div>
                    </div>
                  </Reveal>
                ))}
              </div>
              <div className="text-center mt-12"><Link to="/reviews" className="pill-light">Read all reviews <ArrowUpRight size={14} weight="bold"/></Link></div>
            </>
          ) : (
            <Reveal delay={120}>
              <div className="max-w-2xl mx-auto text-center">
                <div className="flex justify-center gap-1 mb-6">
                  {Array.from({length:5}).map((_,j)=><Star key={`s-${j}`} size={20} weight="fill" className="text-white"/>)}
                </div>
                <p className="text-white/80 font-light leading-relaxed text-lg md:text-xl">
                  We let the work speak for itself — and so do our customers.
                  Once we've sorted your job, we'd love to hear how it went.
                </p>
                <div className="mt-10 flex flex-wrap gap-3 justify-center">
                  <Link to="/reviews" className="pill-light">Leave a review <ArrowUpRight size={14} weight="bold"/></Link>
                  <Link to="/quote" className="pill-ghost">Book a job</Link>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Final CTA — light section (alternating) */}
      <section data-nav-theme="light" className="relative bg-paper text-navy min-h-screen flex items-center py-32 md:py-44 px-5 md:px-10 overflow-hidden">
        <Reveal slow className="relative max-w-[1400px] mx-auto text-center w-full">
          <div className="eyebrow eyebrow-dark">Get in touch · 05</div>
          <h2 className="font-display text-navy mt-6 leading-[0.85] tracking-wide" style={{fontSize:'clamp(4rem, 13vw, 13rem)'}}>GOT A JOB?<br/><span className="text-navy/40">LET'S SORT IT.</span></h2>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/quote" className="pill-navy">Free quote <ArrowUpRight size={14} weight="bold"/></Link>
            <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="text-sm font-medium text-navy hover:text-navy-deep inline-flex items-center gap-2 px-4 py-2">Call {BRAND.phone}</a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
